import { ProfileTracker, PROFILE_TRACKER_STAGE_STATUS } from '../../profile/types/profile-tracker.model';
import { PROFILE_STAGES } from '../../profile/types/profile-stages.model';
import {
  getMembershipFromUser,
  getAllowedFullAccessMembershipStatusIds,
  getTrackerRowForStage,
  isMainOnboardingComplete,
  isApplicationPhaseComplete,
  normalizeRoutePath,
  resolveNextMembershipApplicationSlug,
  resolveUserAccessState,
  sanitizePostLoginRedirectParam,
  MEMBERSHIP_APPLICATION_STAGES,
} from './membership-access';
import {
  hasAuthEmail,
  hasAuthEmailBypass,
  hasAuthPhone,
  hasAuthPhoneBypass,
  isAuthEmailVerified,
  isAuthPhoneVerified,
} from './auth-user-flags';

/**
 * Ordered to match `user-onboarding-set-profile-workflow` activeStep 1–16 (see template *ngIf order).
 * Two UI steps may share one PROFILE_STAGES row (tracker): professional 3–4, causes 12–13.
 * There is no dedicated USER_REVIEW screen in the workflow; see `getFirstSlugForProfileStage`.
 */
export const MAIN_ONBOARDING_SLUGS: ReadonlyArray<{
  slug: string;
  /** PROFILE_STAGES for workflow steps; null for start / thank-you shells */
  stage: PROFILE_STAGES | null;
  uiStep: number;
}> = [
  { slug: 'start', stage: null, uiStep: 0 },
  { slug: 'introduce-yourself', stage: PROFILE_STAGES.USER_INTRODUCE_YOURSELF, uiStep: 1 },
  { slug: 'persona', stage: PROFILE_STAGES.USER_PERSONA, uiStep: 2 },
  { slug: 'linkedin-profile', stage: PROFILE_STAGES.USER_PROFESSIONAL_PROFILE, uiStep: 3 },
  { slug: 'professional-profile', stage: PROFILE_STAGES.USER_PROFESSIONAL_PROFILE, uiStep: 4 },
  { slug: 'strengths', stage: PROFILE_STAGES.USER_STRENGTHS, uiStep: 5 },
  { slug: 'interest-africa', stage: PROFILE_STAGES.USER_INTEREST_IN_AFRICA, uiStep: 6 },
  { slug: 'needs', stage: PROFILE_STAGES.USER_NEEDS, uiStep: 7 },
  { slug: 'requests-offers', stage: PROFILE_STAGES.USER_REQUESTS_OFFERS, uiStep: 8 },
  { slug: 'country-relations', stage: PROFILE_STAGES.USER_COUNTRY_RELATIONS, uiStep: 9 },
  { slug: 'career-roles', stage: PROFILE_STAGES.USER_CAREER_ROLES, uiStep: 10 },
  { slug: 'industries', stage: PROFILE_STAGES.USER_INDUSTRIES, uiStep: 11 },
  { slug: 'causes', stage: PROFILE_STAGES.USER_CAUSES, uiStep: 12 },
  { slug: 'sustainable-development', stage: PROFILE_STAGES.USER_CAUSES, uiStep: 13 },
  { slug: 'organisation-types', stage: PROFILE_STAGES.USER_ORGANISATION_TYPES, uiStep: 14 },
  { slug: 'compensation', stage: PROFILE_STAGES.USER_COMPENSATION, uiStep: 15 },
  { slug: 'thank-you', stage: null, uiStep: 16 },
];

/** Old slugs from pre-alignment routing → canonical slug (same uiStep). */
const LEGACY_WELCOME_SLUG_ALIASES: Readonly<Record<string, string>> = {
  review: 'organisation-types',
  'final-confirmation': 'compensation',
};

function canonicalWelcomeSlug(slug: string): string {
  return LEGACY_WELCOME_SLUG_ALIASES[slug] ?? slug;
}

const SLUG_TO_UI = new Map(MAIN_ONBOARDING_SLUGS.map((e) => [e.slug, e.uiStep]));

export function getMainOnboardingSlugIndex(slug: string): number {
  return MAIN_ONBOARDING_SLUGS.findIndex((e) => e.slug === canonicalWelcomeSlug(slug));
}

export function getUiStepFromWelcomeSlug(slug: string): number {
  return SLUG_TO_UI.get(canonicalWelcomeSlug(slug)) ?? 1;
}

export function getWelcomeSlugForUiStep(uiStep: number): string | undefined {
  return MAIN_ONBOARDING_SLUGS.find((e) => e.uiStep === uiStep)?.slug;
}

/**
 * First workflow slug for a tracker stage (scan order). For stages with two UI steps, returns the earlier step.
 * USER_REVIEW has no screen in the workflow; map to compensation as the last editable step before thank-you.
 */
export function getFirstSlugForProfileStage(stage: PROFILE_STAGES): string | undefined {
  const row = MAIN_ONBOARDING_SLUGS.find((e) => e.stage === stage);
  if (row) {
    return row.slug;
  }
  if (stage === PROFILE_STAGES.USER_REVIEW) {
    return 'compensation';
  }
  if (stage === PROFILE_STAGES.FINAL_CONFIRMATION) {
    return 'thank-you';
  }
  return undefined;
}

/** @deprecated Use getFirstSlugForProfileStage */
export function getWelcomeSlugForProfileStage(stage: PROFILE_STAGES): string | undefined {
  return getFirstSlugForProfileStage(stage);
}

/**
 * First incomplete main-onboarding slug, or `thank-you` when main is done but application phase is not.
 * Does not return membership-application paths — use `resolvePostLoginDestination` for full flow.
 */
export function resolveRequiredWelcomeSlug(
  tracker: ProfileTracker[] | null | undefined,
  stagesLenFallback?: number
): string {
  if (isMainOnboardingComplete(tracker) && !isApplicationPhaseComplete(tracker)) {
    return 'thank-you';
  }
  if (!isMainOnboardingComplete(tracker)) {
    const incomplete = getFirstIncompleteMainOnboardingStage(tracker, stagesLenFallback);
    if (incomplete.stage <= PROFILE_STAGES.USER_WELCOME) {
      return 'start';
    }
    const slug = getFirstSlugForProfileStage(incomplete.stage);
    return slug ?? 'introduce-yourself';
  }
  return 'thank-you';
}

/**
 * Highest index in {@link MAIN_ONBOARDING_SLUGS} the user may be on while onboarding is incomplete.
 * Split stages (two URLs, one tracker row): when status is STARTED, allow both pages until COMPLETED on the last page.
 */
export function getMaxAllowedWelcomeSlugIndex(
  tracker: ProfileTracker[] | null | undefined,
  stagesLenFallback?: number
): number {
  if (isMainOnboardingComplete(tracker) && !isApplicationPhaseComplete(tracker)) {
    return getMainOnboardingSlugIndex('thank-you');
  }
  if (!isMainOnboardingComplete(tracker)) {
    const incomplete = getFirstIncompleteMainOnboardingStage(tracker, stagesLenFallback);
    if (incomplete.stage <= PROFILE_STAGES.USER_WELCOME) {
      return getMainOnboardingSlugIndex('start');
    }
    const row = getTrackerRowForStage(tracker, incomplete.stage);
    const slugsForStage = MAIN_ONBOARDING_SLUGS.filter((e) => e.stage === incomplete.stage);
    if (slugsForStage.length > 1 && Number(row?.status) === PROFILE_TRACKER_STAGE_STATUS.STARTED) {
      const lastSlug = slugsForStage[slugsForStage.length - 1].slug;
      return getMainOnboardingSlugIndex(lastSlug);
    }
    const slug = getFirstSlugForProfileStage(incomplete.stage);
    return getMainOnboardingSlugIndex(slug ?? 'introduce-yourself');
  }
  return getMainOnboardingSlugIndex('thank-you');
}

export function getFirstIncompleteMainOnboardingStage(
  trackerInfo: ProfileTracker[] | null | undefined,
  stagesLen?: number
): { stage: PROFILE_STAGES } {
  if (!trackerInfo?.length) {
    return { stage: PROFILE_STAGES.USER_WELCOME };
  }
  if (trackerInfo.length === 1) {
    const row = trackerInfo[0];
    if (row.stage <= PROFILE_STAGES.FINAL_CONFIRMATION) {
      return row;
    }
    return { stage: PROFILE_STAGES.USER_WELCOME };
  }

  const incomplete = trackerInfo.find(
    (item) =>
      item.stage !== PROFILE_STAGES.USER_SIGNUP &&
      item.stage >= PROFILE_STAGES.USER_INTRODUCE_YOURSELF &&
      item.stage <= PROFILE_STAGES.FINAL_CONFIRMATION &&
      item.status !== PROFILE_TRACKER_STAGE_STATUS.COMPLETED
  );

  if (incomplete) {
    return incomplete;
  }

  if (!stagesLen) {
    return { stage: PROFILE_STAGES.FINAL_CONFIRMATION };
  }
  const mainStageCount = Math.min(PROFILE_STAGES.FINAL_CONFIRMATION, stagesLen);
  if (trackerInfo.length === mainStageCount) {
    return { stage: PROFILE_STAGES.FINAL_CONFIRMATION };
  }
  const lastMain = [...trackerInfo].filter((t) => t.stage <= PROFILE_STAGES.FINAL_CONFIRMATION).pop();
  return { stage: lastMain?.stage ?? PROFILE_STAGES.USER_WELCOME };
}

export interface PostLoginDestination {
  path: string;
  queryParams?: Record<string, string | boolean | null | undefined>;
}

/**
 * Single ordering: email → phone → tracker (main welcome slugs → membership application → review → feed).
 * Used after profile + tracker are available.
 */
export function resolvePostLoginDestination(
  user: any,
  tracker: ProfileTracker[] | null | undefined,
  options?: { postLoginRedirectUrl?: string; postLoginAction?: string; isGoogleProfile?: boolean; orgSwitch?: string }
): PostLoginDestination | null {
  if (!user) {
    return null;
  }

  if (!hasAuthEmailBypass(user)) {
    if (!hasAuthEmail(user)) {
      return { path: '/add-email' };
    }
    if (!isAuthEmailVerified(user)) {
      return { path: '/verify-email' };
    }
  }

  if (!hasAuthPhoneBypass(user)) {
    if (!hasAuthPhone(user) || !isAuthPhoneVerified(user)) {
      return { path: hasAuthPhone(user) ? '/verify-phone-number' : '/add-phone' };
    }
  }

  const membership = getMembershipFromUser(user);
  const allowedIds = getAllowedFullAccessMembershipStatusIds();
  const access = resolveUserAccessState(membership, tracker, allowedIds);

  if (access === 'ONBOARDING') {
    const slug = resolveRequiredWelcomeSlug(tracker);
    const q: Record<string, string | boolean | undefined> = {};
    if (options?.isGoogleProfile) {
      q['gp'] = 'true';
    }
    if (options?.orgSwitch) {
      q['org-switch'] = options.orgSwitch;
    }
    return { path: `/welcome/${slug}`, queryParams: Object.keys(q).length ? q : undefined };
  }

  if (access === 'APPLICATION') {
    const nextSlug = resolveNextMembershipApplicationSlug(tracker);
    if (nextSlug) {
      return { path: `/membership-application/${nextSlug}` };
    }
    return { path: '/welcome/thank-you' };
  }

  if (access === 'BLOCKED') {
    return { path: '/membership-review' };
  }

  const url = sanitizePostLoginRedirectParam(options?.postLoginRedirectUrl) || '/feed';
  return {
    path: url,
    queryParams: options?.postLoginAction ? { action: options.postLoginAction } : undefined,
  };
}

/**
 * Bare `/welcome` only: email/phone gates, then `/welcome/:slug` from the tracker (e.g. `thank-you` during
 * membership application). Never redirects to `/membership-application` — users must be able to return to welcome.
 */
export function resolveWelcomeEntryDestination(
  user: any,
  tracker: ProfileTracker[] | null | undefined,
  options?: { isGoogleProfile?: boolean; orgSwitch?: string }
): PostLoginDestination | null {
  if (!user) {
    return null;
  }

  if (!hasAuthEmailBypass(user)) {
    if (!hasAuthEmail(user)) {
      return { path: '/add-email' };
    }
    if (!isAuthEmailVerified(user)) {
      return { path: '/verify-email' };
    }
  }

  if (!hasAuthPhoneBypass(user)) {
    if (!hasAuthPhone(user) || !isAuthPhoneVerified(user)) {
      return { path: hasAuthPhone(user) ? '/verify-phone-number' : '/add-phone' };
    }
  }

  const slug = resolveRequiredWelcomeSlug(tracker);
  const q: Record<string, string | boolean | undefined> = {};
  if (options?.isGoogleProfile) {
    q['gp'] = 'true';
  }
  if (options?.orgSwitch) {
    q['org-switch'] = options.orgSwitch;
  }
  return { path: `/welcome/${slug}`, queryParams: Object.keys(q).length ? q : undefined };
}

function isAuthEntryPathRequiringRedirect(path: string): boolean {
  return (
    path === '/login' ||
    path === '/join' ||
    path === '/account/password/reset' ||
    path.startsWith('/account/password/reset/')
  );
}

/**
 * Returns true if the user may stay on `currentPath` without being redirected by tracker rules.
 */
export function isCurrentPathAllowedForTrackerState(
  currentPath: string,
  user: any,
  tracker: ProfileTracker[] | null | undefined
): boolean {
  if (!user) {
    return true;
  }

  const path = normalizeRoutePath(currentPath);

  if (!hasAuthEmailBypass(user)) {
    if (!hasAuthEmail(user)) {
      return path === '/add-email' || path === '/change-email';
    }
    if (!isAuthEmailVerified(user)) {
      return ['/verify-email', '/add-email', '/change-email'].some((p) => path === p || path.startsWith(p + '/'));
    }
  }

  if (!hasAuthPhoneBypass(user)) {
    if (!hasAuthPhone(user) || !isAuthPhoneVerified(user)) {
      return ['/add-phone', '/change-phone', '/verify-phone-number'].some((p) => path === p || path.startsWith(p + '/'));
    }
  }

  const membership = getMembershipFromUser(user);
  const allowedIds = getAllowedFullAccessMembershipStatusIds();
  const access = resolveUserAccessState(membership, tracker, allowedIds);

  if (access === 'ONBOARDING') {
    const maxAllowedIdx = getMaxAllowedWelcomeSlugIndex(tracker);
    if (path === '/welcome' || path === '/welcome/') {
      return false;
    }
    if (path.startsWith('/welcome/')) {
      const seg = canonicalWelcomeSlug(path.slice('/welcome/'.length).split('/')[0]);
      const req = getMainOnboardingSlugIndex(seg);
      if (req < 0) {
        return false;
      }
      return req <= maxAllowedIdx;
    }
    return false;
  }

  if (access === 'APPLICATION') {
    const nextSlug = resolveNextMembershipApplicationSlug(tracker);
    const nextIdx = nextSlug ? getMembershipAppSlugIndex(nextSlug) : -1;
    if (path.startsWith('/membership-application/')) {
      const seg = path.slice('/membership-application/'.length).split('/')[0];
      const req = getMembershipAppSlugIndex(seg);
      if (req < 0 || nextIdx < 0) {
        return true;
      }
      return req <= nextIdx;
    }
    if (path === '/welcome' || path.startsWith('/welcome/')) {
      return true;
    }
    return isAllowedExtendedDuringApplication(path);
  }

  if (access === 'BLOCKED') {
    /** Main + application complete: allow revisiting any welcome slug up to thank-you (e.g. edit from membership-review). */
    if (path === '/welcome' || path === '/welcome/') {
      return true;
    }
    if (path.startsWith('/welcome/')) {
      const seg = canonicalWelcomeSlug(path.slice('/welcome/'.length).split('/')[0]);
      const req = getMainOnboardingSlugIndex(seg);
      if (req < 0) {
        return false;
      }
      const maxAllowedIdx = getMaxAllowedWelcomeSlugIndex(tracker);
      return req <= maxAllowedIdx;
    }
    return (
      path === '/membership-review' ||
      path.startsWith('/profile') ||
      path.startsWith('/membership-application') ||
      path.startsWith('/add-email') ||
      path.startsWith('/change-email') ||
      path.startsWith('/verify-email') ||
      path.startsWith('/add-phone') ||
      path.startsWith('/change-phone') ||
      path.startsWith('/verify-phone-number') ||
      path.startsWith('/account/social/connect') ||
      path.startsWith('/account/email/verification') ||
      path.startsWith('/organisation')
    );
  }

  if (access === 'ALLOWED') {
    if (isAuthEntryPathRequiringRedirect(path)) {
      return false;
    }
    if (
      path.startsWith('/welcome') ||
      path.startsWith('/membership-application') ||
      path === '/membership-review'
    ) {
      return false;
    }
    return true;
  }

  return true;
}

function getMembershipAppSlugIndex(slug: string): number {
  return MEMBERSHIP_APPLICATION_STAGES.findIndex((s) => s.slug === slug);
}

function isAllowedExtendedDuringApplication(path: string): boolean {
  return (
    path.startsWith('/profile') ||
    path.startsWith('/add-email') ||
    path.startsWith('/change-email') ||
    path.startsWith('/verify-email') ||
    path.startsWith('/add-phone') ||
    path.startsWith('/change-phone') ||
    path.startsWith('/verify-phone-number') ||
    path.startsWith('/account/social/connect') ||
    path.startsWith('/organisation')
  );
}

/** Previous slug in main onboarding for in-app Back (linear). */
export function getPreviousWelcomeSlug(currentSlug: string): string | null {
  const idx = getMainOnboardingSlugIndex(currentSlug);
  if (idx <= 0) {
    return null;
  }
  return MAIN_ONBOARDING_SLUGS[idx - 1].slug;
}
